import React, { useState } from 'react'
import { View, ViewStyle } from 'react-native'
import { Extrapolation, SharedValue, interpolate, useAnimatedStyle } from 'react-native-reanimated'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ContentLoader, Rect } from '@src/components/ContentLoader'
import { LoadingFastImage, ImageStyle } from '@src/components/LoadingFastImage'
import { LinearGradient } from '@src/components/LinearGradient'
import { useResponsiveStyleSheet } from '@src/hooks/useResponsiveStyleSheet'
import { ProgramWithContent } from '@src/screens/Learn/models/courses.types'
import { ProgressSlider } from '@src/components'
import { getProgramProgress } from '@src/screens/Learn/utils'
import { AnimatedText, Text } from '@components/base'

interface ProgramCardProps {
  program?: ProgramWithContent
  animatedHeight?: SharedValue<number>
  style?: ViewStyle
  verticalTile?: boolean
}

export const PROGRAM_CARD_MAX_HEIGHT = 260
export const PROGRAM_CARD_MIN_HEIGHT = 172
const FONT_SIZE_BASE_TITLE = 30
const FONT_SIZE_BASE_SUBTITLE = 16
const FONT_SIZE_SCALE = 0.75

export const ProgramCard = (props: ProgramCardProps) => {
  const { program, animatedHeight, style, verticalTile = false } = props
  const styles = useResponsiveStyleSheet({ styles: themedStyle, smallScreenStyles })
  const [isImageLoading, setIsImageLoading] = useState(true)
  const progress = getProgramProgress(program) || 0

  const titleFontSize = useAnimatedStyle(() => ({
    fontSize: interpolate(
      animatedHeight?.value || 0,
      [PROGRAM_CARD_MIN_HEIGHT, PROGRAM_CARD_MAX_HEIGHT],
      [FONT_SIZE_BASE_TITLE * FONT_SIZE_SCALE, FONT_SIZE_BASE_TITLE],
      Extrapolation.CLAMP,
    ),
  }))

  const subtitleFontSize = useAnimatedStyle(() => ({
    fontSize: interpolate(
      animatedHeight?.value || 0,
      [PROGRAM_CARD_MIN_HEIGHT, PROGRAM_CARD_MAX_HEIGHT],
      [FONT_SIZE_BASE_SUBTITLE * FONT_SIZE_SCALE, FONT_SIZE_BASE_SUBTITLE],
      Extrapolation.CLAMP,
    ),
  }))

  return (
    <View style={[styles.container, style]}>
      <LoadingFastImage
        source={{ uri: program?.featuredImage?.data?.attributes.url }}
        style={styles.image as ImageStyle}
        onLoad={() => setIsImageLoading(false)}
      >
        <LinearGradient
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          startColor="#000000"
          startOpacity={0}
          endColor="#000000"
          endOpacity={0.6}
          style={styles.image}
        />
      </LoadingFastImage>
      <View style={[styles.content, verticalTile && styles.verticalTileContent]}>
        <AnimatedText
          type={verticalTile ? 'title-3' : 'title-2'}
          numberOfLines={verticalTile ? 2 : undefined}
          ellipsizeMode={verticalTile ? 'tail' : undefined}
          style={[
            !isImageLoading && styles.title,
            verticalTile ? styles.verticalTileTitle : titleFontSize,
          ]}
        >
          {program?.title}
        </AnimatedText>
        {!verticalTile && (
          <AnimatedText
            type="regular"
            style={[!isImageLoading && styles.subtitle, subtitleFontSize]}
          >
            {program?.subtitle}
          </AnimatedText>
        )}
        {verticalTile ? (
          <Text
            type="small"
            lineSpacing="tight"
            numberOfLines={1}
            ellipsizeMode="tail"
            style={[!isImageLoading && styles.verticalTileSubtitle]}
          >
            {program?.subtitle}
          </Text>
        ) : (
          <View style={styles.progressContainer}>
            <ProgressSlider progress={progress} />
            <Text type="regular" style={styles.percentage}>
              {Math.floor(progress * 100)}%
            </Text>
          </View>
        )}
      </View>
    </View>
  )
}

interface LoadingProgramCardProps {
  style?: ViewStyle
}
export const LoadingProgramCard = (props: LoadingProgramCardProps) => {
  const styles = useStyleSheet(themedStyle)

  return (
    <View style={[styles.container, props.style]}>
      <ContentLoader width="100%" height="100%">
        <Rect x="0" y="0" width="100%" height="100%" rx="14" ry="14" />
      </ContentLoader>
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    maxHeight: PROGRAM_CARD_MAX_HEIGHT,
    borderRadius: 14,
    overflow: 'hidden',
    zIndex: 3,
  },
  image: {
    position: 'absolute',
    flex: 1,
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  content: {
    flex: 1,
    justifyContent: 'flex-end',
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  verticalTileContent: {
    paddingHorizontal: 16,
  },
  title: {
    color: 'theme.solid.white',
  },
  verticalTileTitle: {
    fontSize: 14,
    lineHeight: 18,
  },
  subtitle: {
    color: 'theme.solid.white',
    marginTop: 4,
    textTransform: 'uppercase',
  },
  verticalTileSubtitle: {
    color: 'theme.solid.white',
    marginTop: 6,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  percentage: {
    marginLeft: 8,
    color: 'theme.solid.white',
  },
})

const smallScreenStyles = StyleService.create({
  image: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
})
